import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AppService } from 'src/app/services/app/app.service';
import { UsersService } from 'src/app/services/users/users.service';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { GraphService } from 'src/app/services/graph/graph.service';
import { entAnimations } from 'src/app/animations';
import { Subscription, lastValueFrom } from 'rxjs';
import { Services } from 'src/app/services/services/services.service';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Error } from 'src/app/models/error.model';
import { UserInfo } from 'src/app/models/user-info.model';
import { Service } from 'src/app/models/service.model';
import { UserImportFormComponent } from '../user-management/user-import-form/user-import-form.component';
import { UserFormDialogComponent } from '../user-management/user-form/user-form.component';
import { User365FormDialogComponent } from '../m365/user-microsoft-form/user-microsoft-form.component';
import { RouteStatusService } from 'src/app/services/maintenance/route-status.service';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { OrgsService } from 'src/app/services/orgs/orgs.service';
import { UserRightsHelper } from 'src/app/models/role.model';

@Component({
  selector: 'app-support',
  templateUrl: './support.component.html',
  styleUrls: ['./support.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class SupportComponent implements OnInit {
  isLoadingServices = false;
  isLoadingRoles = false;
  isLoadingCorrespondingTable = false;
  selectedUser: UserInfo = new UserInfo();
  searchText = '';
  isServiceLoaded = false;
  hasErrorServiceLoaded = false;
  isRolesLoaded = false;
  hasErrorRolesLoaded = false;
  isCorrespondingTableLoaded = false;
  hasErrorCorrespondingTableLoaded = false;

  isUserAddDisabled = false;
  hasAccessMaintenance = false;
  private subscription: Subscription = new Subscription();

  lastupdateRoleDate: string;
  lastUpdateServiceDate: string;
  lastUpdateCorrespondigTableDate: string;
  filterServices: Service[] = [];
  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 30,
  };

  /**
   * Constructor
   *
   */
  constructor(
    private _appService: AppService,
    private _userService: UsersService,
    private _router: Router,
    private _matDialog: MatDialog,
    private _orgsService: OrgsService,
    private _services: Services,
    private _graphService: GraphService,
    private _routeStatusService: RouteStatusService,
    private _localStorageService: LocalStorageService
  ) {
    this.getUser();
    this.getLastUpdateDateForCache();
  }

  ngOnInit(): void {
    //Maintenance mode settings
    const urls = ['/users-add'];
    this.subscription.add(
      this._routeStatusService
        .getRouteStatusForUrls(urls)
        .subscribe(statuses => {
          this.isUserAddDisabled = urls.some(url => statuses[url]);
        })
    );
  }

  openService(service: Service, lang: string): void {
    if (lang === 'fr') {
      this._services.openService(service.accessurl_fr, service.name);
    } else {
      this._services.openService(service.accessurl_de, service.name);
    }
  }

  openDocumentation(url: string): void {
    this._services.openDocumentation(url);
  }

  userMicrosoftInfo(): void {
    const dialogRef = this._matDialog.open(User365FormDialogComponent, {
      panelClass: 'app-user-microsoft-dialog',
      data: {},
    });

    dialogRef.afterClosed().subscribe((response: FormGroup) => {
      if (!response) {
        return;
      }
    });
  }

  listDeletedTeams(): void {
    this._router.navigate(['/teams/deleted']);
  }

  listGroups(): void {
    this._router.navigate(['/groups']);
  }

  openNemesis(): void {
    window.open('https://d-nemesis.vs.ch/Account/LoginIsm');
  }

  newUser(): void {
    const dialogRef = this._matDialog.open(UserFormDialogComponent, {
      panelClass: 'app-user-form-dialog',
      data: {
        action: 'new',
        currentUser: this.selectedUser,
      },
    });
    dialogRef.afterClosed().subscribe((response: FormGroup) => {
      if (!response) {
        return;
      }
    });
  }

  importUsers(): void {
    const dialogRef = this._matDialog.open(UserImportFormComponent, {
      panelClass: 'app-user-import-form-dialog',
      data: {
        action: 'new',
        currentUser: this.selectedUser,
      },
    });
    dialogRef.afterClosed().subscribe((response: FormGroup) => {
      if (!response) {
        return;
      }
    });
  }

  async getUser(): Promise<void> {
    try {
      // NO User in Cache
      if (this._localStorageService.getItem('currentUser') === null) {
        this.selectedUser = await lastValueFrom(
          this._userService.getCurrentUser()
        );
      } else {
        this.selectedUser = this._userService.currentUserValue;
      }

      // Check if acecss to maintenance page
      if (
        this.selectedUser.roles.some(role => role.name === 'SUPPORTICTVS-EDUID')
      ) {
        this.hasAccessMaintenance = true;
      }

      this.filterServices = this.selectedUser.services.filter(element =>
        element.subtype.includes('support')
      );

      this.filterServices = this.filterServices.sort(
        (a: Service, b: Service) => {
          return a.priority - b.priority;
        }
      );
    } catch (error) {
      let customError: Error = { error: undefined };
      if (error.error) {
        customError = error.error;
      }
      if (customError.error !== undefined) {
        if (customError.error.code == 'ERR403') {
          this._router.navigate(['/errors/error-403']);
        } else {
          this._router.navigate(['/errors/error-404']);
        }
      }
    }
  }

  async fetchService(): Promise<void> {
    this.isLoadingServices = true;
    this.isServiceLoaded = false;
    try {
      this._appService.fetchServices().subscribe();
      await this.getLastUpdateDateForCache();
      this.isLoadingServices = false;
      this.isServiceLoaded = true;
    } catch (error) {
      this.hasErrorServiceLoaded = true;
      let customerError: Error = { error: undefined };
      if (error.error) {
        customerError = error.error;
      }
      if (customerError.error !== undefined) {
        if (customerError.error.code == 'ERR403') {
          this._router.navigate(['/errors/error-403']);
        } else {
          this._router.navigate(['/errors/error-404']);
        }
      }
    }
  }

  async fetchRoles(): Promise<void> {
    this.isLoadingRoles = true;
    this.isRolesLoaded = false;
    try {
      await lastValueFrom(this._orgsService.refreshOrgsCache());
      await this.getLastUpdateDateForCache();
      this.isLoadingRoles = false;
      this.isRolesLoaded = true;
    } catch (error) {
      this.hasErrorRolesLoaded = true;
      let customerError: Error = { error: undefined };
      if (error.error) {
        customerError = error.error;
      }
      if (customerError.error !== undefined) {
        if (customerError.error.code == 'ERR403') {
          this._router.navigate(['/errors/error-403']);
        } else {
          this._router.navigate(['/errors/error-404']);
        }
      }
    }
  }

  async fetchCorrespondingTable(): Promise<void> {
    this.isLoadingCorrespondingTable = true;
    this.isCorrespondingTableLoaded = false;
    try {
      await lastValueFrom(this._graphService.fetchCorrespondingTable());
      await this.getLastUpdateDateForCache();
      this.isLoadingCorrespondingTable = false;
      this.isCorrespondingTableLoaded = true;
    } catch (error) {
      this.hasErrorCorrespondingTableLoaded = true;
      let customerError: Error = { error: undefined };
      if (error.error) {
        customerError = error.error;
      }
      if (customerError.error !== undefined) {
        if (customerError.error.code == 'ERR403') {
          this._router.navigate(['/errors/error-403']);
        } else {
          this._router.navigate(['/errors/error-404']);
        }
      }
    }
  }

  async getLastUpdateDateForCache(): Promise<void> {
    try {
      this.lastupdateRoleDate = await lastValueFrom(
        this._orgsService.getOrgsCacheUpdateDate()
      );

      this.lastUpdateServiceDate = await lastValueFrom(
        this._appService.getLastUpdateDate()
      );

      this.lastUpdateCorrespondigTableDate = await lastValueFrom(
        this._graphService.getLastUpdateDate()
      );
    } catch (error) {
      let customerError: Error = { error: undefined };
      if (error.error) {
        customerError = error.error;
      }
      if (customerError.error !== undefined) {
        if (customerError.error.code == 'ERR403') {
          this._router.navigate(['/errors/error-403']);
        } else {
          this._router.navigate(['/errors/error-404']);
        }
      }
    }
  }

  canAccessToUsers365(): boolean {
    const roleChecker = new UserRightsHelper(
      this.selectedUser.roles,
      this.selectedUser.orgs
    );
    return roleChecker.canAccessToTestFunc();
  }
}
